import { useCallback, useEffect, useState } from 'react';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import {
  Badge, Button, Chip, Empty, Input, Loading,
} from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { colors } from '../../theme';
import { currency, timeAgo } from '../../utils/format';

/**
 * "Your Desire" — the catch-all request form for anything KAAMWALA does not
 * have a category for yet. Admin routes each request to a partner.
 */
const DesirePage = () => {
  const { location } = useApp();
  const toast = useToast();

  const [tab, setTab] = useState('new');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', budget: '', city: location.city || '',
  });

  const load = useCallback(() => {
    api.desires.mine()
      .then((res) => setItems(res.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 3) {
      toast.error('Tell us what you need in a few words.', 'Almost there');
      return;
    }
    setSaving(true);
    try {
      await api.desires.create({
        title: form.title.trim(),
        description: form.description,
        budget: Number(form.budget) || 0,
        address: { city: form.city, coordinates: [location.longitude, location.latitude] },
      });
      setForm({ title: '', description: '', budget: '', city: location.city || '' });
      setTab('requests');
      load();
      toast.success('Our team will find the right person for you and get in touch shortly.', 'Request sent');
    } catch (err) {
      toast.error(err.message, 'Could not send');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <PageHeader title="Your Desire" subtitle="Need something we don't list yet?" />

      <div className="row" style={{ gap: 8, marginBottom: 18 }}>
        <Chip label="✨ New request" active={tab === 'new'} onClick={() => setTab('new')} />
        <Chip
          label={`📋 My requests (${items.length})`}
          active={tab === 'requests'}
          onClick={() => setTab('requests')}
        />
      </div>

      {tab === 'new' ? (
        <Reveal>
          <div className="card sh-sm" style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 40 }}>✨</div>
            <h3 className="h3" style={{ marginTop: 8 }}>Tell us what you need</h3>
            <p className="t-small" style={{ marginTop: 4, lineHeight: 1.6, maxWidth: 460, margin: '4px auto 0' }}>
              Anything from a tailor at home to a tempo for shifting. Our team finds the right person
              near you and calls you back.
            </p>
          </div>

          <form onSubmit={submit} className="card sh-sm">
            <Input
              label="What do you need?"
              icon="📝"
              placeholder="e.g. Need a carpenter to fix a wardrobe"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <Input
              label="More details (optional)"
              textarea
              rows={4}
              placeholder="When you need it, size of the job, anything else"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <div className="form-row">
              <Input
                label="Budget (optional)"
                icon="💰"
                className="grow"
                inputMode="numeric"
                placeholder="2000"
                value={form.budget}
                onChange={(e) => setForm({ ...form, budget: e.target.value.replace(/\D/g, '') })}
              />
              <Input
                label="City"
                className="grow"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
            </div>

            <Button title="Send my request" icon="📤" type="submit" loading={saving} size="lg" full />
          </form>
        </Reveal>
      ) : loading ? <Loading /> : !items.length ? (
        <Empty
          emoji="📋"
          title="No requests yet"
          text="Send your first custom request and track it here."
          actionLabel="New request"
          onAction={() => setTab('new')}
        />
      ) : (
        <div className="grid grid-list">
          {items.map((item, i) => (
            <Reveal key={item._id} delay={stagger(i, 50)} className="card sh-sm">
              <div className="row-between" style={{ alignItems: 'flex-start' }}>
                <div className="grow">
                  <div className="t-title">{item.title}</div>
                  <div className="t-tiny" style={{ marginTop: 2 }}>
                    {item.requestNo} · {timeAgo(item.createdAt)}
                  </div>
                </div>
                <Badge value={item.status} />
              </div>
              {item.description ? (
                <p className="t-small" style={{ marginTop: 10, lineHeight: 1.6 }}>{item.description}</p>
              ) : null}
              <div className="row wrap" style={{ gap: 12, marginTop: 10 }}>
                {item.budget ? <span className="t-tiny">💰 {currency(item.budget)}</span> : null}
                {item.assignedPartner ? (
                  <span className="t-tiny" style={{ color: colors.success }}>
                    ✅ Assigned to {item.assignedPartner.businessName} · {item.assignedPartner.phone}
                  </span>
                ) : null}
              </div>
              {item.adminNote ? (
                <div style={{ background: colors.bg, padding: 12, borderRadius: 12, marginTop: 12 }}>
                  <span className="t-small">💬 {item.adminNote}</span>
                </div>
              ) : null}
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
};

export default DesirePage;
